import { useEffect, useMemo, useState } from "react";
import Icon from "../../components/Icon";
import { StatusBadge, ClientPendingBadge, TaskPriorityBadge } from "../../components/Badges";
import LoadingBlock from "../../components/LoadingBlock";
import EmptyState from "../../components/EmptyState";
import Pagination from "../../components/Pagination";
import TaskDrawer from "../../components/TaskDrawer";
import MultiSelect from "../../components/MultiSelect";
import SearchableSelect from "../../components/SearchableSelect";
import { useApi } from "../../hooks/useApi";
import {
  listEmployeeTasks,
  employeeTaskStats,
  assignEmployeeTask,
  unassignEmployeeTask,
  setEmployeeTaskReviewer,
  deleteEmployeeTask,
  verifyEmployeeTask,
} from "../../api/employeeTaskService";
import { listEmployees } from "../../api/employeeService";
import { listAllBusinesses } from "../../api/businessService";
import { listTaskGroups } from "../../api/taskGroupService";
import { listTaskMasters } from "../../api/taskMasterService";
import { useRefresh } from "../../contexts/RefreshContext";
import { paginatedRows, paginatedMeta } from "../../utils/pagination";
import { formatDateShort, formatDateTime, daysFromToday, initials, analyzeTaskTiming, serialNumber } from "../../utils/formatters";
import { showApiError, showToast } from "../../utils/toast";
import { fetchAndDownload } from "../../utils/exportCsv";

const STATUS_TABS = [
  { id: "all", label: "All", params: {} },
  { id: "pending", label: "Pending", params: { status: "pending" } },
  { id: "in_progress", label: "In progress", params: { status: "in_progress" } },
  { id: "completed", label: "Completed", params: { status: "completed" } },
  { id: "cancelled", label: "Cancelled", params: { status: "cancelled" } },
  { id: "overdue", label: "Overdue", params: { overdue: true } },
  { id: "client_pending", label: "Client pending", params: { client_pending: 1 } },
  { id: "pending_verify", label: "Pending verify", params: { status: "completed", is_verified: 0 } },
  { id: "unassigned", label: "Unassigned", params: { unassigned: true } },
  { id: "oneoff_unbilled", label: "One-off · unbilled", params: { oneoff_no_payment: 1 } },
];

// reviewer_id sentinel "__none__" means "no reviewer set"; buildFilterParams
// maps it to the boolean no_reviewer=1 the backend understands. It can now be
// picked alongside named reviewers — the backend ORs the two.
const NO_REVIEWER = "__none__";

const PRIORITY_OPTIONS = [
  { id: "high", label: "High" },
  { id: "medium", label: "Medium" },
  { id: "low", label: "Low" },
];

const TASK_TYPE_OPTIONS = [
  { id: "one_off", label: "One-off" },
  { id: "recurring", label: "Recurring" },
];

// Multi-select filters hold arrays; the two date filters stay strings. Built by
// a factory so the array literals are never shared between resets.
const makeEmptyFilters = () => ({
  search: "",
  employee_id: [],
  reviewer_id: [],
  business_id: [],
  task_group_id: [],
  task_master_id: [],
  priority: [],
  task_type: [],
  due_from: "",
  due_to: "",
});

const isEmptyFilter = (v) => (Array.isArray(v) ? v.length === 0 : v === "" || v == null);

// Shared by the table query and the chip-count query — they differ only in
// whether the active tab's params are merged on top.
function buildFilterParams(filters, search) {
  const extra = {};
  Object.entries(filters).forEach(([k, v]) => {
    if (isEmptyFilter(v)) return;
    // Raw search is skipped — the debounced copy is appended below, so a
    // keystroke doesn't produce a new request on every character.
    if (k === "search") return;
    // The reviewer dropdown's "__none__" sentinel maps to a boolean
    // no_reviewer=1 rather than a reviewer id. Any real ids picked alongside
    // it still go through, and the backend ORs the two conditions.
    if (k === "reviewer_id") {
      const ids = v.filter((id) => id !== NO_REVIEWER);
      if (ids.length !== v.length) extra.no_reviewer = 1;
      if (ids.length) extra.reviewer_id = ids;
      return;
    }
    extra[k] = v;
  });
  if (search) extra.search = search;
  return extra;
}

export default function TaskOversightPage() {
  const { refreshKey } = useRefresh();
  const [tab, setTab] = useState("all");
  const [filters, setFilters] = useState(makeEmptyFilters);
  const [openTask, setOpenTask] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkEmployeeId, setBulkEmployeeId] = useState("");
  // Reviewer picker. Pairs with the bulk assign action, but also works on its
  // own (no assign action chosen) to set/clear the reviewer while leaving each
  // task's existing assignee untouched:
  //  - "" → leave existing reviewer untouched
  //  - "__clear__" → strip the reviewer from each task
  //  - "<id>" → set this employee as reviewer
  const [bulkReviewerId, setBulkReviewerId] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkVerifying, setBulkVerifying] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  // Server-side ordering for the sortable "#" (task id / creation order) and
  // "Due" headers. Mirrors the backend's default so the first load matches.
  const [sort, setSort] = useState({ by: "due_date", dir: "asc" });
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(filters.search.trim()), 250);
    return () => clearTimeout(t);
  }, [filters.search]);

  const queryParams = useMemo(() => {
    const tabParams = STATUS_TABS.find((f) => f.id === tab)?.params || {};
    return {
      ...tabParams,
      ...buildFilterParams(filters, debouncedSearch),
      sort_by: sort.by,
      sort_dir: sort.dir,
      page,
      per_page: perPage,
    };
  }, [tab, filters, debouncedSearch, sort, page, perPage]);

  // Reset to first page on tab/filter/sort/perPage change.
  useEffect(() => {
    setPage(1);
  }, [tab, filters, debouncedSearch, sort, perPage]);

  const toggleSort = (column) => {
    setSort((s) =>
      s.by === column
        ? { by: column, dir: s.dir === "asc" ? "desc" : "asc" }
        : // Newest-first is the useful default for creation order; earliest-first for dates.
          { by: column, dir: column === "id" ? "desc" : "asc" },
    );
  };

  // Stats omit the active tab params so each chip count reflects its own
  // status slice. Page-level filters (employee, client, group, master, due
  // window) are forwarded so the counts track the visible table.
  const statsParams = useMemo(
    () => buildFilterParams(filters, debouncedSearch),
    [filters, debouncedSearch],
  );

  // Keyed on the serialised params, not object identity: `filters` gets a new
  // identity on every keystroke, which would refetch per character and defeat
  // the debounce above.
  const tasks = useApi(() => listEmployeeTasks(queryParams), [JSON.stringify(queryParams), refreshKey]);
  const stats = useApi(() => employeeTaskStats(statsParams), [JSON.stringify(statsParams), refreshKey]);
  const employees = useApi(() => listEmployees({ per_page: 200 }));
  const clients = useApi(() => listAllBusinesses({ per_page: 500 }));
  const taskGroups = useApi(() => listTaskGroups({ per_page: 200 }));
  const taskMasters = useApi(() => listTaskMasters({ per_page: 500 }));

  const rows = paginatedRows(tasks.data);
  const meta = paginatedMeta(tasks.data);
  const employeeRows = paginatedRows(employees.data);
  const clientRows = paginatedRows(clients.data);
  const groupRows = paginatedRows(taskGroups.data);
  const masterRows = paginatedRows(taskMasters.data);

  // Restrict the Task Master dropdown to the currently selected groups, if any.
  const visibleMasters = useMemo(
    () =>
      filters.task_group_id.length
        ? masterRows.filter((m) =>
            filters.task_group_id.some((g) => String(g) === String(m.task_group_id)),
          )
        : masterRows,
    [masterRows, filters.task_group_id],
  );

  // Option arrays are memoised because MultiSelect filters over them on every
  // keystroke — a fresh array each render would thrash that.
  const employeeOptions = useMemo(
    () => employeeRows.map((e) => ({ id: e.id, label: e.name, hint: e.department?.name })),
    [employeeRows],
  );
  const reviewerOptions = useMemo(
    () => [{ id: NO_REVIEWER, label: "— No reviewer —" }, ...employeeOptions],
    [employeeOptions],
  );
  const clientOptions = useMemo(
    () =>
      clientRows.map((c) => ({
        id: c.id,
        label: `${c.client_id ? `${c.client_id} · ` : ""}${c.business_name}`,
      })),
    [clientRows],
  );
  const groupOptions = useMemo(
    () => groupRows.map((g) => ({ id: g.id, label: g.name })),
    [groupRows],
  );
  const masterOptions = useMemo(
    () => visibleMasters.map((m) => ({ id: m.id, label: m.name, hint: m.task_group?.name })),
    [visibleMasters],
  );

  // Bulk-action pickers stay single-select — a task holds one assignee and one
  // reviewer — but become searchable now that the roster is long.
  const bulkAssignOptions = useMemo(
    () => [
      { id: "__unassign__", label: "Unassign all" },
      ...employeeRows.map((e) => ({
        id: e.id,
        label: `Assign to ${e.name}`,
        hint: e.department?.name,
      })),
    ],
    [employeeRows],
  );
  const bulkReviewerOptions = useMemo(
    () => [
      { id: "__clear__", label: "Clear reviewer" },
      ...employeeRows.map((e) => ({
        id: e.id,
        label: `Set reviewer: ${e.name}`,
        hint: e.department?.name,
      })),
    ],
    [employeeRows],
  );

  const s = stats.data?.data;
  const counts = {
    all: s?.total ?? 0,
    pending: s?.by_status?.pending ?? 0,
    in_progress: s?.by_status?.in_progress ?? 0,
    completed: s?.by_status?.completed ?? 0,
    cancelled: s?.by_status?.cancelled ?? 0,
    overdue: s?.overdue ?? 0,
    client_pending: s?.client_pending ?? 0,
    pending_verify: s?.completed_unverified ?? 0,
    unassigned: s?.unassigned ?? 0,
    oneoff_unbilled: s?.oneoff_unbilled ?? 0,
  };

  const setFilter = (key, value) => {
    setFilters((f) => {
      const next = { ...f, [key]: value };
      // Narrowing the groups drops any picked master that no longer belongs to
      // one of them. Clearing the group filter leaves the masters alone.
      if (key === "task_group_id" && f.task_master_id.length && value.length) {
        next.task_master_id = f.task_master_id.filter((mid) => {
          const master = masterRows.find((m) => String(m.id) === String(mid));
          return !master || value.some((g) => String(g) === String(master.task_group_id));
        });
      }
      return next;
    });
  };

  // Counts the debounced search rather than the raw box, so the badge doesn't
  // tick up mid-typing before the request has even fired.
  const activeFilterCount =
    Object.entries(filters).filter(([k, v]) => k !== "search" && !isEmptyFilter(v)).length +
    (debouncedSearch ? 1 : 0);
  const clearFilters = () => setFilters(makeEmptyFilters());

  // Clear selection on any filter or tab change.
  useEffect(() => {
    setSelected(new Set());
  }, [tab, filters, tasks.data]);

  const visibleIds = useMemo(() => rows.map((t) => t.id), [rows]);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someSelected = !allSelected && visibleIds.some((id) => selected.has(id));

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(visibleIds));
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const exportTasks = async () => {
    setExporting(true);
    try {
      // Backend exports stream the full result; strip per_page and pass filters as-is.
      const { per_page: _ignore, ...filterParams } = queryParams;
      await fetchAndDownload("/employee-tasks/export", filterParams, "task_oversight");
      showToast("success", "Export downloaded");
    } catch (err) {
      showApiError(err);
    } finally {
      setExporting(false);
    }
  };

  const runBulkAssign = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const isUnassign = bulkEmployeeId === "__unassign__";
    const isAssign = bulkEmployeeId !== "" && !isUnassign;
    const reviewerOnly = !bulkEmployeeId && bulkReviewerId !== "";
    // Nothing actionable picked → no-op (mirrors the disabled Apply button).
    if (!bulkEmployeeId && !reviewerOnly) return;

    // Resolve reviewer choice into the third argument of assignEmployeeTask:
    //   undefined → don't touch reviewer
    //   null       → clear reviewer
    //   number     → set reviewer
    let reviewerArg;
    if (bulkReviewerId === "__clear__") reviewerArg = null;
    else if (bulkReviewerId !== "") reviewerArg = Number(bulkReviewerId);
    else reviewerArg = undefined;

    setBulkSaving(true);
    const results = await Promise.allSettled(
      ids.map((id) => {
        if (isUnassign) return unassignEmployeeTask(id);
        if (isAssign) return assignEmployeeTask(id, Number(bulkEmployeeId), reviewerArg);
        // Reviewer-only: keep each task's existing assignee, just set/clear the
        // reviewer via the update endpoint (assign() would force an employee_id).
        return setEmployeeTaskReviewer(id, reviewerArg);
      }),
    );
    setBulkSaving(false);

    const ok = results.filter((r) => r.status === "fulfilled").length;
    const fail = results.length - ok;
    if (ok > 0) {
      const noun = `task${ok === 1 ? "" : "s"}`;
      let message;
      if (isUnassign) {
        message = `Unassigned ${ok} ${noun}`;
      } else if (reviewerOnly) {
        message =
          reviewerArg === null
            ? `Cleared reviewer on ${ok} ${noun}`
            : `Set reviewer on ${ok} ${noun}`;
      } else {
        const reviewerNote =
          reviewerArg === null
            ? " · reviewer cleared"
            : reviewerArg !== undefined
              ? " · reviewer set"
              : "";
        message = `Assigned ${ok} ${noun}${reviewerNote}`;
      }
      showToast("success", message);
    }
    if (fail > 0) {
      const firstErr = results.find((r) => r.status === "rejected")?.reason;
      showApiError(firstErr, `${fail} task${fail === 1 ? "" : "s"} failed to update`);
    }
    setSelected(new Set());
    setBulkEmployeeId("");
    setBulkReviewerId("");
    tasks.reload();
    stats.reload();
  };

  const runBulkVerify = async () => {
    // Only completed-but-unverified tasks can be verified. The Pending-verify
    // tab is already scoped to that slice, but selections can leak across
    // tabs (e.g. user changes tab without clearing), so we filter defensively
    // here and skip ineligible rows rather than fire 422s.
    const eligible = rows
      .filter((t) => selected.has(t.id) && t.status === "completed" && !t.is_verified)
      .map((t) => t.id);

    if (eligible.length === 0) {
      showToast("info", "No selected tasks need verification.");
      return;
    }

    if (!window.confirm(`Verify ${eligible.length} task${eligible.length === 1 ? "" : "s"}? This marks them as reviewed by you.`)) return;

    setBulkVerifying(true);
    const results = await Promise.allSettled(eligible.map((id) => verifyEmployeeTask(id)));
    setBulkVerifying(false);

    const ok = results.filter((r) => r.status === "fulfilled").length;
    const fail = results.length - ok;
    if (ok > 0) showToast("success", `Verified ${ok} task${ok === 1 ? "" : "s"}`);
    if (fail > 0) {
      const firstErr = results.find((r) => r.status === "rejected")?.reason;
      showApiError(firstErr, `${fail} task${fail === 1 ? "" : "s"} failed to verify`);
    }
    setSelected(new Set());
    tasks.reload();
    stats.reload();
  };

  const runBulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} task${ids.length === 1 ? "" : "s"}? This also removes their notes and cannot be undone.`)) return;

    setBulkDeleting(true);
    const results = await Promise.allSettled(ids.map((id) => deleteEmployeeTask(id)));
    setBulkDeleting(false);

    const ok = results.filter((r) => r.status === "fulfilled").length;
    const fail = results.length - ok;
    if (ok > 0) showToast("success", `Deleted ${ok} task${ok === 1 ? "" : "s"}`);
    if (fail > 0) {
      const firstErr = results.find((r) => r.status === "rejected")?.reason;
      showApiError(firstErr, `${fail} task${fail === 1 ? "" : "s"} failed to delete`);
    }
    setSelected(new Set());
    tasks.reload();
    stats.reload();
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Task Oversight</h1>
          <div className="page-sub">Monitor, verify, reassign task instances</div>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={exportTasks} disabled={exporting}>
            <Icon name="download" size={14} /> {exporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>
      </div>

      <div className="filter-bar">
        {STATUS_TABS.map((f) => (
          <button key={f.id} className={`chip ${tab === f.id ? "active" : ""}`} onClick={() => setTab(f.id)}>
            {f.label} <span className="chip-count">{counts[f.id]}</span>
          </button>
        ))}
      </div>

      <div
        className="card"
        style={{
          marginBottom: 12,
          padding: 12,
          display: "grid",
          // A touch wider than the old native selects: the summary text now
          // shares the row with a clear-X and a caret.
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 10,
          alignItems: "end",
        }}
      >
        <div className="field" style={{ gridColumn: "1 / -1" }}>
          <label className="field-label">Search</label>
          <div style={{ position: "relative" }}>
            <Icon
              name="search"
              size={13}
              style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--ink-400)", pointerEvents: "none" }}
            />
            <input
              type="text"
              className="input"
              style={{ paddingLeft: 30 }}
              placeholder="Task, group, client, client ID, assignee or reviewer…"
              value={filters.search}
              onChange={(e) => setFilter("search", e.target.value)}
            />
          </div>
        </div>
        <FilterField label="Due from">
          <input
            type="date"
            className="input"
            value={filters.due_from}
            onChange={(e) => setFilter("due_from", e.target.value)}
          />
        </FilterField>
        <FilterField label="Due to">
          <input
            type="date"
            className="input"
            value={filters.due_to}
            onChange={(e) => setFilter("due_to", e.target.value)}
          />
        </FilterField>
        <FilterField label="Employee">
          <MultiSelect
            value={filters.employee_id}
            onChange={(v) => setFilter("employee_id", v)}
            options={employeeOptions}
            placeholder="All employees"
            searchPlaceholder="Search employees…"
            width="100%"
          />
        </FilterField>
        <FilterField label="Reviewer">
          <MultiSelect
            value={filters.reviewer_id}
            onChange={(v) => setFilter("reviewer_id", v)}
            options={reviewerOptions}
            placeholder="Any reviewer"
            searchPlaceholder="Search reviewers…"
            width="100%"
          />
        </FilterField>
        <FilterField label="Client">
          <MultiSelect
            value={filters.business_id}
            onChange={(v) => setFilter("business_id", v)}
            options={clientOptions}
            placeholder="All clients"
            searchPlaceholder="Search clients…"
            width="100%"
          />
        </FilterField>
        <FilterField label="Task group">
          <MultiSelect
            value={filters.task_group_id}
            onChange={(v) => setFilter("task_group_id", v)}
            options={groupOptions}
            placeholder="All groups"
            searchPlaceholder="Search groups…"
            width="100%"
          />
        </FilterField>
        <FilterField label="Task master">
          <MultiSelect
            value={filters.task_master_id}
            onChange={(v) => setFilter("task_master_id", v)}
            options={masterOptions}
            placeholder="All masters"
            searchPlaceholder="Search masters…"
            width="100%"
          />
        </FilterField>
        <FilterField label="Priority">
          <MultiSelect
            value={filters.priority}
            onChange={(v) => setFilter("priority", v)}
            options={PRIORITY_OPTIONS}
            placeholder="All priorities"
            searchPlaceholder="Search…"
            width="100%"
          />
        </FilterField>
        <FilterField label="Task type">
          <MultiSelect
            value={filters.task_type}
            onChange={(v) => setFilter("task_type", v)}
            options={TASK_TYPE_OPTIONS}
            placeholder="All types"
            searchPlaceholder="Search…"
            width="100%"
          />
        </FilterField>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button
            className="btn btn-ghost"
            onClick={clearFilters}
            disabled={activeFilterCount === 0}
            title={activeFilterCount === 0 ? "No filters set" : "Clear all"}
          >
            <Icon name="x" size={13} /> Clear
            {activeFilterCount > 0 && (
              <span className="chip-count" style={{ marginLeft: 4 }}>{activeFilterCount}</span>
            )}
          </button>
        </div>
      </div>

      {selected.size > 0 && (
        <div
          data-toolbar="true"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "10px 14px",
            background: "var(--primary-50)",
            border: "1px solid var(--primary-200)",
            borderRadius: 8,
            marginBottom: 12,
          }}
        >
          <Icon name="check-square" size={14} style={{ color: "var(--primary-700)" }} />
          <div className="t-primary text-sm fw-500">
            {selected.size} task{selected.size === 1 ? "" : "s"} selected
          </div>
          <div style={{ flex: 1 }} />
          {/* `toolbar-field` is what the ≤720px rule targets to stack these —
              SearchableSelect wraps its trigger in a div, so the toolbar's
              `> .select` selector no longer reaches it. */}
          <div className="toolbar-field" style={{ width: 240 }}>
            <SearchableSelect
              value={bulkEmployeeId}
              onChange={(v) => setBulkEmployeeId(v ?? "")}
              options={bulkAssignOptions}
              placeholder="— Choose action —"
              searchPlaceholder="Search employees…"
              width="100%"
              disabled={bulkSaving}
            />
          </div>
          <div
            className="toolbar-field"
            style={{ width: 220 }}
            title={
              bulkEmployeeId === "__unassign__"
                ? "Reviewer pairs with an assignee — pick an Assign action first"
                : "Optional reviewer for the selected tasks"
            }
          >
            <SearchableSelect
              value={bulkReviewerId}
              onChange={(v) => setBulkReviewerId(v ?? "")}
              options={bulkReviewerOptions}
              placeholder="Keep reviewer"
              searchPlaceholder="Search reviewers…"
              width="100%"
              // Reviewer choice is irrelevant when we're stripping the assignee.
              disabled={bulkSaving || bulkEmployeeId === "__unassign__"}
            />
          </div>
          <button className="btn btn-primary" onClick={runBulkAssign} disabled={bulkSaving || bulkDeleting || bulkVerifying || (!bulkEmployeeId && !bulkReviewerId)}>
            {bulkSaving ? "Applying…" : "Apply"}
          </button>
          {tab === "pending_verify" && (
            <button
              className="btn btn-primary"
              onClick={runBulkVerify}
              disabled={bulkSaving || bulkDeleting || bulkVerifying}
              title="Mark selected completed tasks as verified"
            >
              <Icon name="check" size={13} /> {bulkVerifying ? "Verifying…" : "Verify all"}
            </button>
          )}
          <button
            className="btn btn-secondary"
            onClick={runBulkDelete}
            disabled={bulkSaving || bulkDeleting || bulkVerifying}
            style={{ color: "var(--danger)", borderColor: "var(--danger-bg)" }}
            title="Delete selected tasks"
          >
            <Icon name="trash" size={13} /> {bulkDeleting ? "Deleting…" : "Delete"}
          </button>
          <button className="btn btn-ghost" onClick={() => setSelected(new Set())} disabled={bulkSaving || bulkDeleting || bulkVerifying}>
            Clear
          </button>
        </div>
      )}

      <div className="card">
        <div className="card-body no-pad">
          {tasks.loading ? (
            <div style={{ padding: 16 }}><LoadingBlock rows={6} /></div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon="check-square"
              title="No tasks match the current filters"
              hint={activeFilterCount > 0 ? "Try clearing some filters." : undefined}
              action={
                activeFilterCount > 0 ? (
                  <button className="btn btn-secondary" onClick={clearFilters}>Clear filters</button>
                ) : null
              }
            />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => { if (el) el.indeterminate = someSelected; }}
                      onChange={toggleAll}
                    />
                  </th>
                  <SortHeader label="#" column="id" sort={sort} onSort={toggleSort} className="t-num" style={{ width: 62 }} />
                  <th>Task</th>
                  <th>Business</th>
                  <th>Assignee</th>
                  <th>Reviewer</th>
                  <th>Status</th>
                  <th>Verified</th>
                  <SortHeader label="Due" column="due_date" sort={sort} onSort={toggleSort} className="t-num" />
                  <th>Completed</th>
                  <th>Timing</th>
                  <th style={{ width: 100 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t, i) => (
                  <tr
                    key={t.id}
                    className="clickable"
                    onClick={() => setOpenTask(t.id)}
                    style={selected.has(t.id) ? { background: "var(--primary-50)" } : null}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected.has(t.id)}
                        onChange={() => toggleOne(t.id)}
                      />
                    </td>
                    <td className="t-num t-muted">{serialNumber(meta, i)}</td>
                    <td className="t-primary">{t.task_master?.name}</td>
                    <td className="t-muted">{t.business?.business_name}</td>
                    <td>
                      {t.employee ? (
                        <div className="row gap-sm">
                          <div className="avatar avatar-sm">{initials(t.employee.name)}</div>
                          <span className="t-muted text-xs">{t.employee.name?.split(" ")[0]}</span>
                        </div>
                      ) : (
                        <span className="badge badge-warning">Unassigned</span>
                      )}
                    </td>
                    <td>
                      {t.reviewer ? (
                        <div className="row gap-sm">
                          <div className="avatar avatar-sm">{initials(t.reviewer.name)}</div>
                          <span className="t-muted text-xs">{t.reviewer.name?.split(" ")[0]}</span>
                        </div>
                      ) : (
                        <span className="t-muted">—</span>
                      )}
                    </td>
                    <td>
                      <StatusBadge status={t.status} />
                      {t.is_client_pending && (
                        <div style={{ marginTop: 4 }}><ClientPendingBadge /></div>
                      )}
                    </td>
                    <td>
                      {t.is_verified ? (
                        <span className="badge badge-success"><Icon name="check" size={10} /> Verified</span>
                      ) : t.status === "completed" ? (
                        <span className="badge badge-warning">Pending</span>
                      ) : (
                        <span className="t-muted">—</span>
                      )}
                    </td>
                    <td className="t-num">
                      {daysFromToday(t.due_date) < 0 && t.status !== "completed" ? (
                        <span className="badge badge-danger">{formatDateShort(t.due_date)}</span>
                      ) : (
                        <span className="t-muted">{formatDateShort(t.due_date)}</span>
                      )}
                      <div style={{ marginTop: 4 }}><TaskPriorityBadge priority={t.priority} /></div>
                    </td>
                    <td className="t-num t-muted">
                      {t.completed_at
                        ? formatDateTime(t.completed_at)
                        : t.completion_date
                          ? formatDateShort(t.completion_date)
                          : "—"}
                    </td>
                    <td>
                      {(() => {
                        const a = analyzeTaskTiming(t);
                        const cls = {
                          success: "badge-success",
                          warning: "badge-warning",
                          danger: "badge-danger",
                          info: "badge-info",
                          neutral: "badge-neutral",
                        }[a.tone] || "badge-neutral";
                        return <span className={`badge ${cls}`}>{a.label}</span>;
                      })()}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="row gap-sm" style={{ justifyContent: "flex-end" }}>
                        <button
                          className="icon-btn"
                          title="View"
                          onClick={() => setOpenTask(t.id)}
                        >
                          <Icon name="eye" size={13} />
                        </button>
                        <button
                          className="icon-btn"
                          title="Delete"
                          style={{ color: "var(--danger)" }}
                          onClick={async () => {
                            if (!window.confirm(`Delete task "${t.task_master?.name || ""}"? This also removes its notes.`)) return;
                            try {
                              await deleteEmployeeTask(t.id);
                              showToast("success", "Task deleted");
                              tasks.reload();
                              stats.reload();
                            } catch (err) {
                              showApiError(err);
                            }
                          }}
                        >
                          <Icon name="trash" size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Pagination
            meta={meta}
            page={page}
            onPageChange={setPage}
            perPage={perPage}
            onPerPageChange={setPerPage}
            disabled={tasks.loading}
          />
        </div>
      </div>

      <TaskDrawer
        taskId={openTask}
        role="admin"
        onClose={() => setOpenTask(null)}
        onChanged={() => { tasks.reload(); stats.reload(); }}
      />
    </>
  );
}

// Clickable table header driving the server-side sort. Ordering happens on the
// backend, so the "#" serial (meta.from + row index) stays correct.
function SortHeader({ label, column, sort, onSort, className, style }) {
  const active = sort.by === column;
  const nextDir = active && sort.dir === "asc" ? "descending" : "ascending";
  return (
    <th
      className={`sortable${className ? ` ${className}` : ""}`}
      style={style}
      aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
    >
      <button type="button" onClick={() => onSort(column)} title={`Sort by ${label} (${nextDir})`}>
        {label}
        <Icon
          name={active && sort.dir === "desc" ? "chevron-down" : "chevron-up"}
          size={11}
          style={{ opacity: active ? 1 : 0.3 }}
        />
      </button>
    </th>
  );
}

function FilterField({ label, children }) {
  return (
    <div className="field">
      <label className="field-label">{label}</label>
      {children}
    </div>
  );
}

